import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common';
import SortableTh from '../common/SortableTh';
import { nextSort, sortRows } from '../common/tableSort';
import { fetchAllStudentList, fetchStudentListPage } from './studentListApi';
import { ChevronLeft, ChevronRight, Phone, ClipboardList, Loader2 } from 'lucide-react';
import { useDesktopExpanded } from '../../hooks/useDesktopExpanded';
import useStudentRatings from '../../hooks/useStudentRatings';
import StudentRatingCell from './StudentRatingCell';
import '../../styles/desktop-expanded.css';

const EMPTY_STUDENTS = [];

/**
 * 학생 목록 테이블 패널 (StudentsHub 좌측 마스터)
 *
 * StudentManagement.js 의 목록 로직(데이터/필터/학교급 핫키/페이지네이션/테이블)을
 * 독립 컴포넌트로 이식한 것. 기존 StudentManagement.js 는 건드리지 않는다.
 *
 * @param {boolean} compact          좌우 분할 시 폭 축소 모드(연락처 숨김, 레이팅 유지)
 * @param {number|null} selectedStudentId  선택행 강조용 학생 ID
 * @param {(student:object)=>void} onSelectStudent  행 클릭 콜백
 *
 * 주: 페이지(page)는 컴포넌트 내부 상태로 관리한다(선택과 직교). URL ?page= 동기화는 후속 과제.
 */
function StudentTablePanel({ compact = false, selectedStudentId = null, onSelectStudent, ratingRevision = 0 }) {
  const navigate = useNavigate();
  const { getAccessToken, userId, userType } = useAuth();
  const { isMobileView } = useDesktopExpanded();

  const [ratingSort, setRatingSort] = useState(null);
  const sortRef = useRef(null);
  // 요청의 모드·계정을 결과와 묶는다. 정렬 전환 시 전체 행을 페이지 행으로 잘못 조회하지 않는다.
  const [listRequest, setListRequest] = useState(null);
  const [studentList, setStudentList] = useState(null);
  const mode = ratingSort ? 'all' : 'page';
  const scopeKey = JSON.stringify([userType, userId]);
  const currentRequest = listRequest?.mode === mode && listRequest.scope === scopeKey ? listRequest : null;
  const listReady = !!currentRequest && studentList?.request === currentRequest;
  const students = listReady ? studentList.rows : EMPTY_STUDENTS;
  const { getRating, retryStudent, loading: ratingsLoading, error: ratingError } = useStudentRatings(students, {
    mode, listRequest: currentRequest, listReady, refreshKey: ratingRevision,
  });
  const requestControllerRef = useRef(null);
  const [listError, setListError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [serverTotalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [teachers, setTeachers] = useState([]);
  const [isTeachersLoading, setIsTeachersLoading] = useState(false);
  const [filters, setFilters] = useState({
    student_name: '',
    in_charge_teacher: '',
    lecture: '',
    school_name: ''
  });
  const [selectedSchoolType, setSelectedSchoolType] = useState(null);
  const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
  const [isFilterMode, setIsFilterMode] = useState(false);
  const tableContainerRef = useRef(null);
  // '조회'로 제출된 필터 스냅샷 — 페이지네이션·리사이즈 재요청은 입력창의 라이브 값이 아니라 이 값을 쓴다.
  const appliedFiltersRef = useRef({ student_name: '', in_charge_teacher: '', lecture: '', school_name: '' });
  // 동시 요청 경쟁 방지 시퀀스 — 최신 요청 응답만 상태에 반영한다.
  const requestSeqRef = useRef(0);
  // 리사이즈 재요청이 항상 최신 상태(page·isFilterMode·students·isMobileView)를 읽도록 하는 최신 클로저 홀더.
  const resizeFetchRef = useRef(null);

  // 좁은 폭(모바일 or compact)에서는 축약형 셀/필터를 사용
  const narrow = compact || isMobileView;
  // 셀 좌우 패딩·행 모서리·페이지네이션은 데스크톱(원본, compact=false) 스타일을 따르고
  // 실제 모바일에서만 압축한다. compact 분할뷰(440px)도 원본 데스크톱 스타일을 그대로 사용.
  const tight = isMobileView;
  const showContactColumn = !narrow;     // 연락처/학부모 컬럼
  // 분할 목록은 학생·학교·담당·레이팅 네 열을 유지한다. 목록 점수는 16px로 고정한다.
  const fixedCols = compact;

  // 화면 크기에 따라 표시할 수 있는 행 수 계산 (StudentManagement 동일)
  const calculateItemsPerPage = () => {
    try {
      const windowHeight = window.innerHeight;
      const buffer = 10;
      let availableHeight, rowHeight;

      if (isMobileView) {
        const bypassLayoutHeight = windowHeight - 50 - 68;
        const cardHeight = bypassLayoutHeight - 29;
        const cardInternalFixed = 154 + 12 + 35 + 40 + buffer;
        availableHeight = cardHeight - cardInternalFixed;
        rowHeight = 48;
      } else {
        const cardHeight = windowHeight - 96;
        const cardInternalFixed = 32 + 28 + 80 + 16 + 40 + 60 + buffer;
        availableHeight = cardHeight - cardInternalFixed;
        rowHeight = 48;
      }

      const itemsCount = Math.floor(availableHeight / rowHeight);
      const minItems = 5;
      const maxItems = isMobileView ? 30 : 50;
      return Math.max(minItems, Math.min(itemsCount, maxItems));
    } catch (error) {
      console.error('Error calculating items per page:', error);
      return 10;
    }
  };

  const totalPages = ratingSort ? Math.max(1, Math.ceil(students.length / pageSize)) : serverTotalPages;
  const hasMore = page < totalPages;
  const sortingPending = !!ratingSort && (ratingsLoading || students.some((student) => getRating(student.student_id).status === 'loading'));
  const errorMessage = listError || (ratingSort ? ratingError : null);
  const waiting = isLoading || !listReady || sortingPending;
  const failedRatings = ratingSort ? students.filter((student) => getRating(student.student_id).status === 'error').length : 0;
  // 동점은 학생 번호 오름차순. 점수 미확인은 sortRows 규칙에 따라 양쪽 방향 모두 마지막이다.
  const sortedStudents = ratingSort ? sortRows(
    [...students].sort((a, b) => Number(a.student_id) - Number(b.student_id)),
    ratingSort,
    (student) => {
      const entry = getRating(student.student_id);
      return entry.status === 'ready' ? entry.rating : null;
    },
  ) : students;
  const displayedStudents = waiting || errorMessage ? [] : ratingSort
    ? sortedStudents.slice((page - 1) * pageSize, page * pageSize) : students;

  const fetchStudents = async (pageNum = 1, useFilter = false) => {
    const seq = ++requestSeqRef.current;
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    const sorting = !!sortRef.current;
    const applied = useFilter ? { ...appliedFiltersRef.current } : {};
    const request = { seq, mode: sorting ? 'all' : 'page', scope: scopeKey };
    const itemsPerPage = calculateItemsPerPage();
    const options = { filters: applied, userType, userId, signal: controller.signal };
    setIsLoading(true);
    setListError(null);
    setPageSize(itemsPerPage);
    // 같은 학생 목록이 돌아와도 새 요청 객체로 점수를 다시 조회한다(페이지 재진입·조회 버튼).
    setListRequest(request);
    setStudentList(null);
    try {
      if (sorting) {
        const rows = await fetchAllStudentList(options, getAccessToken);
        if (seq !== requestSeqRef.current) return;
        setStudentList({ request, rows });
        setTotalItems(rows.length);
        const resolvedPage = Math.min(pageNum, Math.max(1, Math.ceil(rows.length / itemsPerPage)));
        setPage(resolvedPage);
        setPageInputValue(String(resolvedPage));
      } else {
        const result = await fetchStudentListPage({ ...options, page: pageNum, perPage: itemsPerPage }, getAccessToken);
        if (seq !== requestSeqRef.current) return;
        if (pageNum > result.totalPages) return fetchStudents(result.totalPages, useFilter);
        setStudentList({ request, rows: result.rows });
        setTotalPages(result.totalPages);
        setTotalItems(result.totalItems ?? result.rows.length);
        setPage(pageNum);
        setPageInputValue(String(pageNum));
      }
    } catch (error) {
      if (seq !== requestSeqRef.current || controller.signal.aborted) return;
      if (error.httpStatus === 401) { navigate('/signin'); return; }
      if (!sorting && error.httpStatus === 404 && pageNum > 1) return fetchStudents(1, useFilter);
      setStudentList(null);
      setTotalPages(1);
      setTotalItems(0);
      setPage(1);
      setPageInputValue('1');
      setListError(sorting
        ? '학생 목록을 모두 불러오지 못해 정렬을 완료하지 못했습니다. 다시 조회해주세요.'
        : '학생 목록을 불러오지 못했습니다. 다시 조회해주세요.');
    } finally {
      if (seq === requestSeqRef.current) setIsLoading(false);
    }
  };

  const handleRatingSort = (key) => {
    const previous = sortRef.current;
    const next = nextSort(previous, key);
    sortRef.current = next;
    setRatingSort(next);
    setPage(1);
    setPageInputValue('1');
    // 방향만 바꾸면 진행 중 조회 또는 이미 받은 전체 목록을 그대로 사용한다.
    if (!!previous !== !!next) fetchStudents(1, isFilterMode);
  };

  const fetchTeachers = async () => {
    try {
      setIsTeachersLoading(true);
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/teacher?page=1&per_page=50`,
        { headers: { 'Authorization': token, 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error('강사 목록을 불러오는데 실패했습니다.');
      const result = await response.json();
      const filteredTeachers = result.data?.filter(teacher =>
        ['원장', '강사', '팀장'].includes(teacher.charge_work)
      ) || [];
      setTeachers(filteredTeachers);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    } finally {
      setIsTeachersLoading(false);
    }
  };

  const handleSearch = () => {
    // 제출 시점의 입력값을 스냅샷 → 이후 페이지네이션·리사이즈 재요청이 이 조건을 사용.
    appliedFiltersRef.current = { ...filters };
    setIsFilterMode(true);
    fetchStudents(1, true);
  };

  const handleReset = () => {
    sortRef.current = null;
    setRatingSort(null);
    const empty = { student_name: '', in_charge_teacher: '', lecture: '', school_name: '' };
    setIsFilterMode(false);
    setFilters(empty);
    setSelectedSchoolType(null);
    appliedFiltersRef.current = empty;
    fetchStudents(1, false);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || waiting || errorMessage) return;
    if (sortRef.current) {
      setPage(newPage);
      setPageInputValue(String(newPage));
    } else fetchStudents(newPage, isFilterMode);
  };

  const handleRetry = () => {
    fetchStudents(1, isFilterMode);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSchoolTypeFilter = (schoolType, forceSearch = true) => {
    setSelectedSchoolType(schoolType);
    if (schoolType) {
      const hotkeyMap = {
        elementary_school: '초등학교',
        middle_school: '중학교',
        high_school: '고등학교'
      };
      setShouldAutoSearch(true);
      setFilters(prev => ({ ...prev, school_name: hotkeyMap[schoolType] }));
    } else {
      if (forceSearch) setShouldAutoSearch(true);
      setFilters(prev => ({ ...prev, school_name: '' }));
    }
  };

  // 계정 전환·화면 재진입은 이전 목록과 진행 중 요청을 폐기한다.
  useEffect(() => {
    fetchStudents(1, isFilterMode);
    if (userType === 'manager') fetchTeachers();
    return () => { requestSeqRef.current += 1; requestControllerRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, userId]);

  // 리사이즈 재요청용 최신 클로저 — 렌더마다 최신 page·isFilterMode·students·fetchStudents(=최신 isMobileView)를 캡처.
  resizeFetchRef.current = () => {
    const size = calculateItemsPerPage();
    if (size === pageSize) return;
    if (sortRef.current) {
      const resolvedPage = Math.min(page, Math.max(1, Math.ceil(students.length / size)));
      setPageSize(size);
      setPage(resolvedPage);
      setPageInputValue(String(resolvedPage));
    } else if (students.length > 0) fetchStudents(page, isFilterMode);
  };

  // 채점·마감·재오픈으로 순서가 바뀌면 최신 점수의 첫 페이지를 보여 준다.
  useEffect(() => {
    if (sortRef.current) { setPage(1); setPageInputValue('1'); }
  }, [ratingRevision]);

  // 화면 크기 변경 시 per_page 재계산 (마운트당 1회 등록 + 200ms 디바운스 + 최신 클로저 참조로 stale 방지)
  useEffect(() => {
    let timer = null;
    const handleResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { if (resizeFetchRef.current) resizeFetchRef.current(); }, 200);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 학교 이름 필터 ↔ 핫키 상태 동기화
  useEffect(() => {
    if (!filters.school_name) {
      setSelectedSchoolType(null);
    } else {
      const reverseHotkeyMap = { '초등학교': 'elementary_school', '중학교': 'middle_school', '고등학교': 'high_school' };
      const matchedHotkey = reverseHotkeyMap[filters.school_name];
      if (matchedHotkey !== selectedSchoolType) {
        setSelectedSchoolType(matchedHotkey || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.school_name]);

  // 핫키로 필터 변경 시 자동 검색
  useEffect(() => {
    if (shouldAutoSearch) {
      setShouldAutoSearch(false);
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.school_name, shouldAutoSearch]);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(start + maxVisible - 1, totalPages);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    const renderInput = (i, mobile) => {
      const inputWidth = mobile
        ? Math.max(2, String(pageInputValue).length * 0.6 + 1.2)
        : Math.max(2.5, String(pageInputValue).length * 0.6 + 1.5);
      return (
        <input
          key={i}
          type="text"
          value={pageInputValue}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || /^\d+$/.test(value)) setPageInputValue(value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const newPage = parseInt(pageInputValue);
              if (newPage >= 1 && newPage <= totalPages) {
                e.target.blur();
                setTimeout(() => handlePageChange(newPage), 100);
              } else {
                setPageInputValue(String(page));
              }
            }
          }}
          onMouseUp={(e) => e.preventDefault()}
          onFocus={(e) => e.target.select()}
          onBlur={() => setPageInputValue(String(page))}
          className={mobile
            ? 'flex items-center justify-center rounded-full border border-primary bg-primary text-white text-xs font-bold'
            : 'page-button active font-bold'}
          style={mobile
            ? { width: `${inputWidth}rem`, minWidth: '2rem', height: '2rem', textAlign: 'center', outline: 'none', cursor: 'text', padding: '0 4px' }
            : { borderRadius: '1.5rem', width: `${inputWidth}rem`, minWidth: '2.5rem', height: '40px', textAlign: 'center', outline: 'none', border: 'none', cursor: 'text', padding: '0 8px' }}
          aria-label="페이지 번호 입력"
        />
      );
    };

    const mobile = tight;
    const numBtnClass = mobile
      ? 'w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs'
      : 'page-button';
    const dotsClass = mobile ? 'px-1 text-gray-400' : 'page-dots';
    const numBtnStyle = mobile ? {} : { borderRadius: '1.5rem' };

    if (start > 1) {
      pageNumbers.push(
        <button key={1} className={numBtnClass} style={numBtnStyle} onClick={() => handlePageChange(1)}>1</button>
      );
      if (start > 2) pageNumbers.push(<span key="ellipsis1" className={dotsClass}>...</span>);
    }
    for (let i = start; i <= end; i++) {
      if (page === i) {
        pageNumbers.push(renderInput(i, mobile));
      } else {
        pageNumbers.push(
          <button key={i} className={numBtnClass} style={numBtnStyle} onClick={() => handlePageChange(i)} aria-label={`${i} 페이지`}>{i}</button>
        );
      }
    }
    if (end < totalPages) {
      if (end < totalPages - 1) pageNumbers.push(<span key="ellipsis2" className={dotsClass}>...</span>);
      pageNumbers.push(
        <button key={totalPages} className={numBtnClass} style={numBtnStyle} onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
      );
    }
    return pageNumbers;
  };

  // 필터 입력 그룹 (좁은 폭: 2열 그리드 / 넓은 폭: 1행)
  const renderFilters = () => {
    const teacherField = userType !== 'teacher' ? (
      <select
        name="in_charge_teacher"
        value={filters.in_charge_teacher}
        onChange={handleFilterChange}
        className={`${narrow ? 'w-full' : 'flex-1'} rounded border px-2 py-1 text-xs bg-white`}
        disabled={isTeachersLoading}
      >
        <option value="">{isTeachersLoading ? '강사 목록 로딩중...' : '담당 강사'}</option>
        {teachers.map((teacher) => (
          <option key={teacher.user_id} value={teacher.user_name}>
            {teacher.user_name} ({teacher.charge_work})
          </option>
        ))}
      </select>
    ) : (
      <input
        type="text"
        name="in_charge_teacher"
        placeholder="담당 강사"
        value={filters.in_charge_teacher}
        onChange={handleFilterChange}
        onKeyDown={handleKeyPress}
        className={`${narrow ? 'w-full' : 'flex-1'} rounded border px-2 py-1 text-xs`}
        disabled
      />
    );

    const inputCls = `${narrow ? 'w-full' : 'flex-1'} rounded border px-2 py-1 text-xs`;

    return (
      <div>
        <div className="mt-3 flex gap-2">
          <div className={narrow ? 'flex-1 grid gap-2 grid-cols-2' : 'flex-1 flex gap-2'}>
            <input type="text" name="student_name" placeholder="학생 이름" value={filters.student_name} onChange={handleFilterChange} onKeyDown={handleKeyPress} className={inputCls} />
            <input type="text" name="school_name" placeholder="학교 이름" value={filters.school_name || ''} onChange={handleFilterChange} onKeyDown={handleKeyPress} className={inputCls} />
            {teacherField}
            <input type="text" name="lecture" placeholder="강의" value={filters.lecture} onChange={handleFilterChange} onKeyDown={handleKeyPress} className={inputCls} />
          </div>
          <div className={narrow ? 'flex w-16 flex-col gap-2' : 'flex gap-2'}>
            <Button onClick={handleReset} variant="secondary" size="sm" rounded="3xl" className={`${narrow ? 'flex-1' : 'px-6'} min-h-[22px]`}>초기화</Button>
            <Button onClick={handleSearch} variant="primary" size="sm" rounded="3xl" className={`${narrow ? 'flex-1' : 'px-6'} min-h-[22px]`}>조회</Button>
          </div>
        </div>

        {/* 학교급 핫키 */}
        <div className="mt-3">
          <div className={`flex gap-2 ${narrow ? 'overflow-x-auto' : ''}`}>
            {[
              { key: 'elementary_school', label: '초등학교' },
              { key: 'middle_school', label: '중학교' },
              { key: 'high_school', label: '고등학교' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleSchoolTypeFilter(selectedSchoolType === key ? null : key, true)}
                className={`flex-1 px-4 py-1.5 text-sm transition-all duration-200 ${
                  selectedSchoolType === key ? 'bg-primary text-white font-bold' : 'bg-gray-100 text-gray-700 font-medium'
                }`}
                style={{ borderRadius: '20px' }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      {/* 헤더 영역 */}
      <div className="shrink-0 border-b" style={{ padding: narrow ? '12px' : '16px' }}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <h3 className="text-lg font-semibold">학생 목록</h3>
            <p className="truncate text-xs text-gray-500">
              - 총 <span className="font-semibold text-primary">{totalItems || 0}</span>명 중{' '}
              <span className="font-semibold text-primary">{displayedStudents.length}</span>명 표시 중
            </p>
          </div>
          {/* 「전체 테스트」 — 학생을 고른 좁은 상태에서도 보여야 한다(오너 지시 2026-09-06).
              종전에는 `!compact` 안에 있어 학생을 고르는 순간 사라졌다.
              누르면 학생 선택이 풀리면서 전체 폭 테스트 목록으로 간다 — 경로가 다르기 때문이고,
              「전체」 테스트는 학생 한 명이 아니라 전부를 보는 화면이라 그것이 맞다.
              좁은 폭에서는 글자만 줄인다(Ky Test → Ky 와 같은 자). 아이콘이 같아 같은 버튼으로 읽힌다. */}
          <Button
            variant="primary"
            onClick={() => navigate('/students-hub/tests')}
            title="전체 테스트"
            className="text-xs rounded-3xl !bg-white !text-primary !border !border-primary hover:!bg-primary/10 whitespace-nowrap"
          >
            <ClipboardList size={14} className="mr-1" />
            {compact ? '테스트' : '전체 테스트'}
          </Button>
        </div>
        {renderFilters()}
      </div>

      {/* 컨텐츠 영역 */}
      <div className="flex-1 overflow-y-auto" style={{ marginTop: '8px', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {errorMessage && (
          <div role="alert" className="flex items-center justify-center gap-2 px-3 py-4 text-xs text-danger">
            <span>{errorMessage}</span>
            <button type="button" className="shrink-0 underline" onClick={handleRetry}>다시 조회</button>
          </div>
        )}
        {!waiting && !errorMessage && failedRatings > 0 && (
          <div role="status" className="px-3 py-2 text-xs text-gray-500">점수를 확인하지 못한 학생 {failedRatings}명은 마지막에 표시합니다.</div>
        )}
          <div ref={tableContainerRef} className="overflow-x-auto flex-1" style={{ minHeight: 0, width: '100%' }}>
            <table aria-busy={waiting} className={`w-full ${fixedCols ? 'table-fixed' : ''}`}>
              {fixedCols && (
                <colgroup>
                  <col style={{ width: '34%' }} />
                  <col style={{ width: '24%' }} />
                  <col />
                  <col style={{ width: '96px' }} />
                </colgroup>
              )}
              <thead className="bg-gray-50">
                <tr>
                  <th className={`${tight ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>학생/이메일</th>
                  <th className={`${tight ? 'px-2' : 'px-3'} py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>학교/학년</th>
                  <th className={`${tight ? 'px-2' : 'px-3'} py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>담당/강의</th>
                  {showContactColumn && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">연락처/학부모</th>
                  )}
                  <SortableTh sortKey="rating" sort={ratingSort} onSort={handleRatingSort} className={`${tight ? 'px-2' : 'px-3'} whitespace-nowrap py-2 text-right text-xs font-medium text-gray-500`}>레이팅</SortableTh>
                </tr>
              </thead>
              <tbody className="bg-white">
                {waiting && !errorMessage && (
                  <tr>
                    <td colSpan={showContactColumn ? 5 : 4} className="py-12 text-center">
                      <span role="status" aria-label="학생 목록 조회 중" className="inline-flex">
                        <Loader2 aria-hidden="true" className="size-6 animate-spin text-primary" />
                      </span>
                    </td>
                  </tr>
                )}
                {displayedStudents.map((student) => {
                  const isSelected = selectedStudentId === student.student_id;
                  return (
                    <tr
                      key={student.student_id}
                      onClick={() => onSelectStudent && onSelectStudent(student)}
                      className={`cursor-pointer transition-colors duration-150 border-b border-gray-100 ${
                        isSelected
                          ? 'bg-primary/15'
                          : student.is_blocked
                          ? 'bg-red-50 hover:bg-primary/10'
                          : 'hover:bg-primary/10'
                      }`}
                    >
                      {/* 학생/이메일 */}
                      <td className={`${tight ? 'px-1 rounded-l-xl max-w-[90px]' : 'px-3 rounded-l-3xl'} py-2 whitespace-nowrap transition-colors duration-150`}>
                        <div className="flex items-center">
                          <div className={`flex-shrink-0 ${narrow ? 'h-6 w-6' : 'h-8 w-8'}`}>
                            <div className={`${narrow ? 'h-6 w-6 text-xs' : 'h-8 w-8 text-sm'} rounded-full flex items-center justify-center text-white font-bold ${
                              student.is_blocked ? 'bg-gradient-to-r from-red-500 to-red-600' : 'bg-gradient-to-r from-primary to-primary/70'
                            }`}>
                              {student.student_id}
                            </div>
                          </div>
                          <div className={`${narrow ? 'ml-1 min-w-0 overflow-hidden' : 'ml-3'}`}>
                            {narrow ? (
                              <>
                                <div className={`text-xs font-medium text-gray-900 leading-tight ${fixedCols ? 'truncate' : 'whitespace-nowrap'}`}>{student.student_name}</div>
                                <div className={`text-xs text-gray-500 truncate leading-tight ${fixedCols ? '' : 'max-w-[55px]'}`}>{student.student_email}</div>
                              </>
                            ) : (
                              <div className="flex items-center gap-3">
                                <div className="text-sm font-medium text-gray-900 whitespace-nowrap">{student.student_name}</div>
                                <div className="text-xs text-gray-500">{student.student_email}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* 학교/학년 */}
                      <td className={`${tight ? 'px-1 max-w-[70px]' : 'px-3'} py-2 whitespace-nowrap transition-colors duration-150`}>
                        <div className="text-sm">
                          {narrow ? (
                            <>
                              <div className={`text-xs text-gray-900 truncate leading-tight ${fixedCols ? '' : 'max-w-[60px]'}`}>{student.school_name}</div>
                              <div className="text-xs text-gray-500 whitespace-nowrap leading-tight">{student.school_grade}학년</div>
                            </>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="text-sm text-gray-900 whitespace-nowrap">{student.school_name}</div>
                              <div className="text-xs text-gray-500">({student.school_grade}학년)</div>
                            </div>
                          )}
                        </div>
                      </td>
                      {/* 담당/강의 */}
                      <td className={`${tight ? 'px-1 max-w-[60px]' : 'px-3'} py-2 whitespace-nowrap transition-colors duration-150`}>
                        <div className="text-sm">
                          {narrow ? (
                            <>
                              <div className={`text-xs text-gray-900 truncate leading-tight ${fixedCols ? '' : 'max-w-[50px]'}`}>{student.in_charge_teacher || '-'}</div>
                              <div className={`text-xs text-gray-500 truncate leading-tight ${fixedCols ? '' : 'max-w-[50px]'}`}>{student.lecture || '-'}</div>
                            </>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="text-sm text-gray-900 whitespace-nowrap">{student.in_charge_teacher || '-'}</div>
                              {student.lecture && <div className="text-xs text-gray-500">({student.lecture})</div>}
                            </div>
                          )}
                        </div>
                      </td>
                      {/* 연락처/학부모 */}
                      {showContactColumn && (
                        <td className="px-3 py-2 whitespace-nowrap transition-colors duration-150">
                          <div className="text-sm">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1 text-sm text-gray-900 whitespace-nowrap">
                                <Phone className="w-3 h-3 text-gray-400" />
                                <span>{student.mobile_number}</span>
                              </div>
                              <div className="text-xs text-gray-500">(학부모: {student.parent_mobile_number})</div>
                            </div>
                          </div>
                        </td>
                      )}
                      <td className={`${tight ? 'px-2 rounded-r-xl' : 'px-3 rounded-r-3xl'} py-2 whitespace-nowrap text-right align-middle`}>
                        <StudentRatingCell
                          entry={getRating(student.student_id)}
                          studentName={student.student_name}
                          onRetry={() => retryStudent(student.student_id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        {/* 페이지네이션 */}
        {!waiting && !errorMessage && displayedStudents.length > 0 && (
          <div className="shrink-0 bg-white pb-4">
            <div className={tight ? 'flex justify-center items-center gap-2' : 'desktop-pagination'}>
              <button
                className={tight ? 'w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed' : 'page-button'}
                style={!tight ? { borderRadius: '1.5rem' } : {}}
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                aria-label="이전 페이지"
              >
                {tight ? '<' : <ChevronLeft size={16} className="font-bold" />}
              </button>
              <div className="flex gap-1">{renderPageNumbers()}</div>
              <button
                className={tight ? 'w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed' : 'page-button'}
                style={!tight ? { borderRadius: '1.5rem' } : {}}
                onClick={() => handlePageChange(page + 1)}
                disabled={!hasMore}
                aria-label="다음 페이지"
              >
                {tight ? '>' : <ChevronRight size={16} className="font-bold" />}
              </button>
            </div>
          </div>
        )}

        {!waiting && !errorMessage && displayedStudents.length === 0 && (
          <div className="py-8 text-center text-gray-500">학생 데이터가 없습니다.</div>
        )}
      </div>
    </div>
  );
}

export default StudentTablePanel;
